import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Users, TrendingUp, ChevronRight, Loader2, GitCompare, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Players() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from player name
  const getPlayerSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const togglePlayerSelection = (playerName) => {
    if (selectedPlayers.includes(playerName)) {
      setSelectedPlayers(selectedPlayers.filter(p => p !== playerName));
    } else if (selectedPlayers.length < 3) {
      setSelectedPlayers([...selectedPlayers, playerName]);
    }
  };

  const handleCompare = () => {
    if (selectedPlayers.length >= 2) {
      const slugs = selectedPlayers.map(name => getPlayerSlug(name));
      navigate(`/players/${slugs[0]}?compare=${slugs.slice(1).join(',')}`);
    }
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setSelectedPlayers([]);
  };

  return (
    <Layout>
      <Helmet>
        <title>NBA Player Prop Analysis Hub | PrimeStatLab</title>
        <meta name="description" content="In-depth player prop analysis for NBA players. Historical picks, stats, and betting insights for every player we cover." />
        <link rel="canonical" href="https://primestatlab.com/players" />
      </Helmet>

      <div className="container mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                <Users className="inline-block mr-3 text-primary" size={40} />
                Player Analysis Hub
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Deep-dive analysis on every player we cover. View historical picks, 
                performance trends, and detailed breakdowns.
              </p>
            </div>
            
            {/* Compare Mode Toggle */}
            {!loading && players.length >= 2 && (
              <div className="flex-shrink-0">
                {!compareMode ? (
                  <Button
                    onClick={() => setCompareMode(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="enable-compare-btn"
                  >
                    <GitCompare size={18} />
                    Compare Players
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleCompare}
                      disabled={selectedPlayers.length < 2}
                      className="flex items-center gap-2"
                      data-testid="compare-selected-btn"
                    >
                      <GitCompare size={18} />
                      Compare ({selectedPlayers.length}/3)
                    </Button>
                    <Button
                      onClick={exitCompareMode}
                      variant="ghost"
                      size="icon"
                      data-testid="exit-compare-btn"
                    >
                      <X size={18} />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Compare Mode Instructions */}
          {compareMode && (
            <div className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-sm text-primary">
                Select 2-3 players to compare. Click the checkbox on each player card.
              </p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-primary" size={40} />
          </div>
        )}

        {/* Players Grid */}
        {!loading && players.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {players.map((player) => {
              const isSelected = selectedPlayers.includes(player.name);
              const slug = getPlayerSlug(player.name);
              
              return (
                <div
                  key={player.name}
                  className={`relative group bg-card border rounded-lg p-6 transition-all ${
                    compareMode 
                      ? isSelected 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-border hover:border-muted-foreground cursor-pointer'
                      : 'border-border hover:border-primary'
                  }`}
                  onClick={compareMode ? () => togglePlayerSelection(player.name) : undefined}
                  data-testid={`player-card-${slug}`}
                >
                  {/* Checkbox for compare mode */}
                  {compareMode && (
                    <div 
                      className={`absolute top-3 right-3 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-primary border-primary' 
                          : 'border-muted-foreground hover:border-primary'
                      }`}
                      data-testid={`compare-checkbox-${slug}`}
                    >
                      {isSelected && <Check size={14} className="text-black" />}
                    </div>
                  )}
                  
                  {/* Card Content - Wrapped in Link only when not in compare mode */}
                  {compareMode ? (
                    <div className="pr-8">
                      <div className="flex justify-between items-start mb-4">
                        <h2 className="text-xl font-heading font-bold">
                          {player.name}
                        </h2>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Picks:</span>
                          <span className="font-bold">{player.total_picks}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Record:</span>
                          <span className="font-bold">
                            <span className="text-green-500">{player.wins}W</span>
                            {' - '}
                            <span className="text-red-500">{player.losses}L</span>
                            {player.pending > 0 && (
                              <span className="text-yellow-500"> - {player.pending}P</span>
                            )}
                          </span>
                        </div>
                        {player.total_picks > 0 && player.wins + player.losses > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Win Rate:</span>
                            <span className={`font-bold ${
                              (player.wins / (player.wins + player.losses)) >= 0.5 
                                ? 'text-green-500' 
                                : 'text-red-500'
                            }`}>
                              {Math.round((player.wins / (player.wins + player.losses)) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Link to={`/players/${slug}`} className="block">
                      <div className="flex justify-between items-start mb-4">
                        <h2 className="text-xl font-heading font-bold group-hover:text-primary transition-colors">
                          {player.name}
                        </h2>
                        <ChevronRight className="text-muted-foreground group-hover:text-primary transition-colors" size={20} />
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Picks:</span>
                          <span className="font-bold">{player.total_picks}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Record:</span>
                          <span className="font-bold">
                            <span className="text-green-500">{player.wins}W</span>
                            {' - '}
                            <span className="text-red-500">{player.losses}L</span>
                            {player.pending > 0 && (
                              <span className="text-yellow-500"> - {player.pending}P</span>
                            )}
                          </span>
                        </div>
                        {player.total_picks > 0 && player.wins + player.losses > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Win Rate:</span>
                            <span className={`font-bold ${
                              (player.wins / (player.wins + player.losses)) >= 0.5 
                                ? 'text-green-500' 
                                : 'text-red-500'
                            }`}>
                              {Math.round((player.wins / (player.wins + player.losses)) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <span className="text-xs text-primary font-mono uppercase tracking-wider">
                          View Full Analysis →
                        </span>
                      </div>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && players.length === 0 && (
          <div className="text-center py-20">
            <Users className="mx-auto text-muted-foreground mb-4" size={60} />
            <h2 className="text-xl font-heading mb-2">No Players Yet</h2>
            <p className="text-muted-foreground">
              Player hubs will appear here as picks are added.
            </p>
          </div>
        )}

        {/* SEO Content */}
        <div className="mt-16 prose prose-invert max-w-none">
          <h2 className="text-2xl font-heading">About Our Player Analysis</h2>
          <p className="text-muted-foreground">
            Each player hub contains comprehensive prop betting analysis including recent form, 
            matchup history, statistical trends, and our historical pick performance. 
            We track every pick we make so you can see our actual results.
          </p>
        </div>
      </div>
    </Layout>
  );
}
